import { Component, EventEmitter, Input, Output, OnInit, AfterViewInit, ElementRef, ViewChild, HostListener, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PrimengSharedModule } from '../../primeng/primeng-shared/primeng-shared.module';
import { HttpClient, HttpClientModule, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export interface FieldOption { value: string | number; label: string }
export type FieldType = 'text' | 'number' | 'date' | 'time' | 'textarea' | 'select' | 'autocomplete' | 'tel' | 'email' | 'checkbox';
export interface FieldConfig {
  id: string;            // ID y key en el objeto de valores
  label: string;         // Etiqueta visible
  type?: FieldType;      // Tipo de input
  placeholder?: string;  // Placeholder en edición
  options?: FieldOption[]; // Para selects locales o preinyectados
  // Carga dinámica de opciones (select)
  optionsUrl?: string;   // Endpoint para obtener opciones (GET)
  optionLabelKey?: string; // Clave del label en la respuesta (default: 'label')
  optionValueKey?: string; // Clave del value/uuid en la respuesta (default: 'uuid' -> si no existe, 'value')
  optionsQueryParam?: string; // Nombre del parámetro de búsqueda si aplica (default: none)
  // Autocomplete
  autocompleteUrl?: string; // Endpoint para sugerencias (GET)
  autocompleteQueryParam?: string; // Nombre del parámetro que envía el término (default: 'q')
  minLength?: number; // Longitud mínima para disparar autocomplete (default: 2)
  // Alternativa: función observable para completar sugerencias
  autocompleteFn?: (query: string) => Observable<FieldOption[]>;
  readonly?: boolean;    // Si aplica en modo edición
}

export interface SectionConfig {
  title: string;
  fields: FieldConfig[];
  editable?: boolean;
}

@Component({
  selector: 'app-form-view-edit-format',
  standalone: true,
  imports: [CommonModule, FormsModule, PrimengSharedModule, HttpClientModule],
  templateUrl: './form-view-edit-format.component.html',
  styleUrls: ['./form-view-edit-format.component.scss']
})
export class FormViewEditFormatComponent implements OnInit, AfterViewInit, OnChanges {
  // Inputs generales
  @Input() title = 'Solicitud';
  @Input() subtitle = 'Resumen';
  @Input() requestKind?: string; // clave opcional para la solicitud
  @Input() sections: SectionConfig[] = [];
  @Input() values: Record<string, any> = {};
  @Input() isSubmitting = false;
  @Input() editButton = true;
  @Input() startInEdit = false;
  @Input() ticketNotches = false; // activa las muescas tipo ticket sin overflow
  // Secciones no editables por título
  @Input() nonEditableSectionTitles: string[] = ['Información adicional'];
  // Guardado automático opcional
  @Input() saveEndpoint?: string; // Si se establece, al guardar se hace POST/PUT automáticamente
  @Input() saveMethod: 'POST' | 'PUT' = 'POST';
  @Input() saveHeaders?: Record<string, string>;
  @Input() additionalPayload?: Record<string, any>; // payload adicional a enviar junto a values
  // Control externo de guardado y avisos
  @Input() saveDisabled: boolean = false; // permite deshabilitar el botón Guardar desde el padre
  @Input() bannerMessage?: string | null; // mensaje de banner en modo edición
  @Input() bannerSeverity?: 'info' | 'warn' | 'success' | 'error' | null = null; // estilo del banner

  // Outputs
  @Output() save = new EventEmitter<Record<string, any>>();
  @Output() cancel = new EventEmitter<void>();
  @Output() valuesChange = new EventEmitter<Record<string, any>>();
  @Output() editModeChange = new EventEmitter<boolean>();

  // Flip state
  isEdit = false;
  private ro?: ResizeObserver;
  currentHeight = 0;

  // Estados para selects/autocomplete
  acSuggestions: Record<string, FieldOption[]> = {};
  acModel: Record<string, any> = {}; // modelo visible para autocomplete (objeto opción)

  @ViewChild('flipInner') flipInner?: ElementRef<HTMLElement>;
  @ViewChild('frontFace') frontFace?: ElementRef<HTMLElement>;
  @ViewChild('backFace') backFace?: ElementRef<HTMLElement>;

  constructor(private http: HttpClient) { }

  ngOnInit(): void {
    this.isEdit = !!this.startInEdit;
  }

  ngAfterViewInit(): void {
    // Altura estable: máximo entre frente y dorso; observar cambios de tamaño
    this.ro = new ResizeObserver(() => this.syncFlipHeight());
    const front = this.frontFace?.nativeElement;
    const back = this.backFace?.nativeElement;
    if (front) this.ro.observe(front);
    if (back) this.ro.observe(back);
    setTimeout(() => this.syncFlipHeight(), 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Si el padre reconstruye secciones/valores mientras estamos en edición, re-sincronizamos
    if (this.isEdit && (changes['sections'] || changes['values'])) {
      setTimeout(() => {
        this.syncAutocompleteModelsFromValues();
        this.syncFlipHeight();
      }, 0);
    }
  }

  toggleEdit() {
    this.isEdit = !this.isEdit;
    this.editModeChange.emit(this.isEdit);
    // Altura ya es el máx, pero forzamos un chequeo por si el dorso difiere mucho
    setTimeout(() => this.syncFlipHeight(), 0);
    if (this.isEdit) {
      // Prefetch de opciones dinámicas al entrar a edición
      this.prefetchDynamicData();
      // Sincronizar modelos de autocomplete con valores existentes
      this.syncAutocompleteModelsFromValues();
    }
  }

  onInputChange(fieldId: string, value: any) {
    // Actualiza valores y emite para two-way binding externo
    this.values = { ...this.values, [fieldId]: value };
    this.valuesChange.emit(this.values);
    // Cambios de contenido pueden variar altura
    setTimeout(() => this.syncFlipHeight(), 0);
  }

  onSubmit(): void {
    if (this.saveEndpoint) {
      this.isSubmitting = true;
      const method = (this.saveMethod || 'POST').toUpperCase();
      const headers = new HttpHeaders(this.saveHeaders || { 'Content-Type': 'application/json' });
      // Normalize autocomplete selections into values before creating body
      try {
        for (const section of this.editableSections) {
          for (const field of section.fields || []) {
            if ((field.type || 'text') === 'autocomplete') {
              const id = field.id;
              const model = this.acModel[id];
              const displayKey = `${id}__display`;
              const maybeObjKey = id.endsWith('_id') ? id.replace(/_id$/, '') : null;
              if (model && typeof model === 'object' && 'value' in model) {
                this.values[id] = (model as any).value;
                this.values[displayKey] = (model as any).label ?? String((model as any).value);
                if (maybeObjKey) (this.values as any)[maybeObjKey] = { id: (model as any).value };
              } else if (model == null && this.values[id] != null) {
                // cleared
                this.values[id] = null;
                this.values[displayKey] = null;
                if (maybeObjKey) (this.values as any)[maybeObjKey] = null;
              }
            }
          }
        }
      } catch { }
      const body = { ...(this.additionalPayload || {}), ...(this.values || {}) };
      const req = method === 'PUT'
        ? this.http.put(this.saveEndpoint, body, { headers })
        : this.http.post(this.saveEndpoint, body, { headers });
      req.subscribe({
        next: (resp: any) => {
          // Fusionar la respuesta para reflejar datos actualizados (ej: usuario_modifica)
          this.values = { ...(this.values || {}), ...(resp || {}) };
          this.valuesChange.emit(this.values);
          this.save.emit({ ...this.values, __response: resp });
          this.isSubmitting = false;
          this.isEdit = false;
          setTimeout(() => this.syncFlipHeight(), 50); // ligero delay para render
        },
        error: () => {
          this.isSubmitting = false; // dejar que el padre maneje el error vía toasts, etc.
        }
      });
    } else {
      // Normalize autocomplete selections into values before emitting
      try {
        for (const section of this.editableSections) {
          for (const field of section.fields || []) {
            if ((field.type || 'text') === 'autocomplete') {
              const id = field.id;
              const model = this.acModel[id];
              const displayKey = `${id}__display`;
              const maybeObjKey = id.endsWith('_id') ? id.replace(/_id$/, '') : null;
              if (model && typeof model === 'object' && 'value' in model) {
                this.values[id] = (model as any).value;
                this.values[displayKey] = (model as any).label ?? String((model as any).value);
                if (maybeObjKey) (this.values as any)[maybeObjKey] = { id: (model as any).value };
              } else if (model == null && this.values[id] != null) {
                // cleared
                this.values[id] = null;
                this.values[displayKey] = null;
                if (maybeObjKey) (this.values as any)[maybeObjKey] = null;
              }
            }
          }
        }
      } catch { }
      this.save.emit(this.values);
    }
  }

  onCancel(): void {
    this.isEdit = false;
    this.cancel.emit();
    setTimeout(() => this.syncFlipHeight(), 0);
  }

  getOptionLabel(field: FieldConfig, value: any): string {
    if (value === undefined || value === null) return String(value ?? '—');
    const displayKey = `${field.id}__display`;
    if (this.values && displayKey in this.values) {
      const displayVal = this.values[displayKey];
      if (displayVal) return String(displayVal);
    }
    const ac = this.acModel?.[field.id];
    if (ac && ac.value === value && ac.label) return ac.label;
    if (!field.options) return String(value);
    const found = field.options.find(o => o.value === value);
    return found ? found.label : String(value);
  }

  @HostListener('window:resize')
  onWindowResize() { this.syncFlipHeight(); }

  private syncFlipHeight() {
    const inner = this.flipInner?.nativeElement;
    const front = this.frontFace?.nativeElement;
    const back = this.backFace?.nativeElement;
    if (!inner || !front || !back) return;

    const frontH = front.scrollHeight;
    const backH = back.scrollHeight;
    const h = Math.max(frontH, backH);
    if (h !== this.currentHeight) {
      this.currentHeight = h;
      inner.style.height = `${h}px`;
    }
  }
  get editableSections(): SectionConfig[] {
    const blocked = (this.nonEditableSectionTitles || []).map(t => (t || '').trim().toLowerCase());
    return (this.sections || []).filter(s => {
      const byFlag = s.editable !== false; // default true
      const byTitle = !blocked.includes((s.title || '').trim().toLowerCase());
      return byFlag && byTitle;
    });
  }

  private prefetchDynamicData() {
    for (const section of this.editableSections) {
      for (const field of section.fields || []) {
        if ((field.type === 'select') && field.optionsUrl && (!field.options || field.options.length === 0)) {
          this.loadOptionsFor(field).subscribe();
        }
        // para autocomplete no pre-cargamos hasta que escriban, salvo que ya tengamos un valor y queramos mostrar etiqueta
        if (field.type === 'autocomplete' && this.values[field.id] != null) {
          // Intentar obtener la opción actual para mostrar en el input
          this.ensureAutocompleteOptionLoaded(field, String(this.values[field.id]));
        }
      }
    }
  }

  private loadOptionsFor(field: FieldConfig): Observable<FieldOption[]> {
    const url = field.optionsUrl!;
    const labelKey = field.optionLabelKey || 'label';
    const valueKey = field.optionValueKey || 'uuid';
    return this.http.get<any[]>(url).pipe(
      map((data: any[]) => (Array.isArray(data) ? data : []).map((it: any) => ({
        label: String(it?.[labelKey] ?? it?.label ?? ''),
        value: it?.[valueKey] ?? it?.value
      }))),
      tap((opts) => {
        field.options = opts;
        if (field.type === 'autocomplete') this.syncAutocompleteModel(field);
      })
    );
  }

  onAutoComplete(field: FieldConfig, event: { query: string }) {
    const q = event?.query ?? '';
    const min = field.minLength ?? 2;
    if (q.length < min) {
      this.acSuggestions[field.id] = [];
      return;
    }
    const labelKey = field.optionLabelKey || 'label';
    const valueKey = field.optionValueKey || 'uuid';
    if (field.autocompleteFn) {
      field.autocompleteFn(q).subscribe({
        next: (opts) => { this.acSuggestions[field.id] = opts || []; },
        error: () => { this.acSuggestions[field.id] = []; }
      });
    } else if (field.autocompleteUrl) {
      const qp = field.autocompleteQueryParam || 'q';
      const params = new HttpParams().set(qp, q);
      this.http.get<any[]>(field.autocompleteUrl, { params }).subscribe({
        next: (data: any[]) => {
          this.acSuggestions[field.id] = (Array.isArray(data) ? data : []).map((it: any) => ({
            label: String(it?.[labelKey] ?? it?.label ?? ''),
            value: it?.[valueKey] ?? it?.value
          }));
        },
        error: () => { this.acSuggestions[field.id] = []; }
      });
    } else if (field.options) {
      const lower = q.toLowerCase();
      this.acSuggestions[field.id] = field.options.filter(o => o.label.toLowerCase().includes(lower));
    } else {
      this.acSuggestions[field.id] = [];
    }
  }

  onAutoSelect(field: FieldConfig, event: any) {
    const evVal = event?.value ?? event;
    let selectedId: string | number | undefined;
    let selectedLabel: string | undefined;
    if (evVal && typeof evVal === 'object') {
      selectedId = (evVal as any).value ?? (evVal as any).id;
      selectedLabel = (evVal as any).label ?? (evVal as any).nombreCompleto ?? ((evVal as any).nombre || undefined);
    } else {
      selectedId = evVal as any;
    }
    if (selectedId == null || selectedId === '') return;
    const sel: FieldOption = { value: selectedId as any, label: String(selectedLabel ?? selectedId) };
    this.acModel[field.id] = sel;
    const displayKey = `${field.id}__display`;
    const maybeObjKey = field.id.endsWith('_id') ? field.id.replace(/_id$/, '') : null;
    this.values = {
      ...this.values,
      [field.id]: sel.value,
      [displayKey]: sel.label,
      ...(maybeObjKey ? { [maybeObjKey]: { id: sel.value } } : {})
    };
    this.valuesChange.emit(this.values);
    setTimeout(() => this.syncFlipHeight(), 0);
  }

  onAutoClear(field: FieldConfig) {
    this.acModel[field.id] = null;
    const displayKey = `${field.id}__display`;
    const maybeObjKey = field.id.endsWith('_id') ? field.id.replace(/_id$/, '') : null;
    this.values = {
      ...this.values,
      [field.id]: null,
      [displayKey]: null,
      ...(maybeObjKey ? { [maybeObjKey]: null } : {})
    };
    this.valuesChange.emit(this.values);
    setTimeout(() => this.syncFlipHeight(), 0);
  }

  private syncAutocompleteModelsFromValues() {
    for (const section of this.editableSections) {
      for (const field of section.fields || []) {
        if (field.type === 'autocomplete') {
          this.syncAutocompleteModel(field);
        }
      }
    }
  }

  private syncAutocompleteModel(field: FieldConfig) {
    const val = this.values[field.id];
    if (val == null) return;
    const found = (field.options || []).find(o => o.value === val);
    if (found) {
      this.acModel[field.id] = found;
      return;
    }
    // Fallback: si no hay opciones precargadas, usar el display del valor ya presente
    const displayKey = `${field.id}__display`;
    const display = this.values && displayKey in this.values ? this.values[displayKey] : undefined;
    if (display) {
      this.acModel[field.id] = { value: val, label: String(display) };
    }
  }

  private ensureAutocompleteOptionLoaded(field: FieldConfig, value: string) {
    this.syncAutocompleteModel(field);
  }

  // --- Visibilidad en vista (frente): ocultar campos vacíos -----------------
  shouldShow(field: FieldConfig): boolean {
    const v = this.values ? this.values[field.id] : undefined;
    if (v === undefined || v === null) return false;
    // Fechas: mostrar solo si hay una fecha válida
    if ((field.type || 'text') === 'date') {
      const d = v instanceof Date ? v : (v ? new Date(v) : null);
      return !!(d && !isNaN(d.getTime()));
    }
    if (typeof v === 'string') return v.trim().length > 0;
    if (typeof v === 'number') return !isNaN(v) && v !== 0;
    if (typeof v === 'boolean') return v === true;
    // Para selects/autocomplete con objetos
    if (typeof v === 'object') return Object.keys(v || {}).length > 0;
    return true;
  }

}
